import React, { useState } from 'react';
import { Star } from 'lucide-react';
import { AreaChart, Area, ResponsiveContainer, YAxis } from 'recharts';
import { marketData, colors } from '../../utils/portfolioData';
type TabType = 'Favorites' | 'Top Traded' | 'Gainers' | 'Losers' | 'New';
function TrendChart({
  data,
  isPositive
}: {
  data: number[];
  isPositive: boolean;
}) {
  const chartData = data.map((val, i) => ({
    i,
    val
  }));
  const color = isPositive ? colors.success : colors.danger;
  const minVal = Math.min(...data);
  const maxVal = Math.max(...data);
  const range = maxVal - minVal;
  const padding = range * 0.1;
  return <div className="h-12 w-32" data-id="element-400">
      <ResponsiveContainer width="100%" height="100%" data-id="element-401">
        <AreaChart data={chartData} margin={{
        left: 0,
        right: 0
      }} data-id="element-402">
          <defs data-id="element-403">
            <linearGradient id={`gradient-${isPositive}`} x1="0" y1="0" x2="0" y2="1" data-id="element-404">
              <stop offset="5%" stopColor={color} stopOpacity={0.2} data-id="element-405" />
              <stop offset="95%" stopColor={color} stopOpacity={0} data-id="element-406" />
            </linearGradient>
          </defs>
          <YAxis hide domain={[minVal - padding, maxVal + padding]} type="number" data-id="element-407" />
          <Area type="monotone" dataKey="val" stroke={color} strokeWidth={1.5} fill={`url(#gradient-${isPositive})`} isAnimationActive={false} data-id="element-408" />
        </AreaChart>
      </ResponsiveContainer>
    </div>;
}
export function MarketSnapshot() {
  const [activeTab, setActiveTab] = useState<TabType>('Favorites');
  const tabs: TabType[] = ['Favorites', 'Top Traded', 'Gainers', 'Losers', 'New'];
  return <div className="bg-background-secondary border border-border-subtle rounded-[20px] overflow-hidden" data-id="element-409">
      {/* Header with Tabs */}
      <div className="border-b border-border-subtle" data-id="element-410">
        <div className="flex items-center justify-between px-6 py-4" data-id="element-411">
          <div className="flex items-center gap-1" data-id="element-412">
            {tabs.map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`px-3 py-1.5 text-sm font-ui rounded-lg transition-all ${activeTab === tab ? 'bg-background-elevated text-text-primary' : 'text-text-secondary hover:text-text-primary hover:bg-background-elevated/50'}`} data-id="element-413">
                {tab === 'Favorites' && <Star size={14} className="inline mr-1.5" data-id="element-414" />}
                {tab}
              </button>)}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto" data-id="element-415">
        <table className="w-full" data-id="element-416">
          <thead className="bg-background-elevated/50" data-id="element-417">
            <tr className="border-b border-border-subtle" data-id="element-418">
              <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-left" data-id="element-419">
                Market
              </th>
              <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-420">
                Price
              </th>
              <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-421">
                24H Low
              </th>
              <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-422">
                24H High
              </th>
              <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-423">
                24H Chg.
              </th>
              <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-424">
                24H Trend
              </th>
            </tr>
          </thead>
          <tbody data-id="element-425">
            {marketData.map(market => <tr key={market.symbol} className="border-b border-border-subtle hover:bg-background-elevated/30 transition-colors group" data-id="element-426">
                {/* Market */}
                <td className="px-6 py-4" data-id="element-427">
                  <div className="flex items-center gap-3" data-id="element-428">
                    <img src={market.logo} alt={market.name} className="w-6 h-6 object-contain" data-id="element-429" />
                    <div data-id="element-430">
                      <div className="text-sm font-medium text-text-primary" data-id="element-431">
                        {market.symbol}
                      </div>
                      <div className="text-xs text-text-secondary" data-id="element-432">
                        {market.leverage}
                      </div>
                    </div>
                  </div>
                </td>

                {/* Price */}
                <td className="px-6 py-4 text-right" data-id="element-433">
                  <div className="font-numbers text-sm text-text-primary" data-id="element-434">
                    {market.price.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
                    <span className="text-text-secondary text-xs ml-1" data-id="element-435">
                      USD
                    </span>
                  </div>
                </td>

                {/* 24H Low */}
                <td className="px-6 py-4 text-right" data-id="element-436">
                  <div className="font-numbers text-sm text-text-primary" data-id="element-437">
                    {market.low24h.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
                    <span className="text-text-secondary text-xs ml-1" data-id="element-438">
                      USD
                    </span>
                  </div>
                </td>

                {/* 24H High */}
                <td className="px-6 py-4 text-right" data-id="element-439">
                  <div className="font-numbers text-sm text-text-primary" data-id="element-440">
                    {market.high24h.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
                    <span className="text-text-secondary text-xs ml-1" data-id="element-441">
                      USD
                    </span>
                  </div>
                </td>

                {/* 24H Change */}
                <td className="px-6 py-4 text-right" data-id="element-442">
                  <div className={`font-numbers text-sm ${market.change24h >= 0 ? 'text-success' : 'text-danger'}`} data-id="element-443">
                    {market.change24h >= 0 ? '+' : ''}
                    {market.change24h.toFixed(2)}%
                  </div>
                </td>

                {/* 24H Trend */}
                <td className="px-6 py-4" data-id="element-444">
                  <div className="flex justify-end" data-id="element-445">
                    <TrendChart data={market.priceHistory} isPositive={market.change24h >= 0} data-id="element-446" />
                  </div>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}